import { useEffect, useRef, useState } from 'react'
import { useT } from '../i18n'

/**
 * Démonstration du sélecteur, dans un MacBook dessiné en CSS.
 *
 * Pas d'image : une capture vieillit mal, pèse lourd et se floute sur les écrans
 * Retina. Le rendu vectoriel reste net partout et se retouche en une ligne.
 *
 * L'animation s'arrête hors écran et se désactive si le système demande à réduire
 * les animations — une boucle infinie qui tourne dans un onglet de fond est une
 * ponction inutile sur la batterie.
 */

interface Win {
  title: string
  app: string
  hue: number
}

const WINDOWS: Win[] = [
  { title: 'AppSwitch.swift', app: 'Xcode', hue: 212 },
  { title: 'Design System', app: 'Figma', hue: 275 },
  { title: 'Inbox — Work', app: 'Mail', hue: 205 },
  { title: 'Chill Beats', app: 'Spotify', hue: 140 },
  { title: 'Roadmap Q3', app: 'Notion', hue: 30 },
  { title: 'Budget 2026', app: 'Numbers', hue: 95 },
  { title: 'zsh — main', app: 'Terminal', hue: 220 },
  { title: 'Architecture', app: 'Excalidraw', hue: 340 },
]

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(query.matches)
    const listener = (e: MediaQueryListEvent) => setReduced(e.matches)
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
  }, [])
  return reduced
}

export default function MacBook() {
  const t = useT()
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const frame = useRef<HTMLDivElement>(null)
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    const node = frame.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry?.isIntersecting ?? true),
      { threshold: 0.15 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (reduced || !visible) return
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % WINDOWS.length),
      1400
    )
    return () => window.clearInterval(timer)
  }, [reduced, visible])

  const current = WINDOWS[index]!

  return (
    <div className="mac" ref={frame}>
      <div className="mac-lid">
        <div className="mac-screen">
          <div className="mac-notch" />

          <div className="mac-menubar">
            <span className="mac-menu-apple" />
            <span className="mac-menu-item is-bold">Xcode</span>
            <span className="mac-menu-item">File</span>
            <span className="mac-menu-item">Edit</span>
            <span className="mac-menu-item">View</span>
            <span className="mac-menubar-spacer" />
            <span className="mac-menu-glyph is-accent" />
            <span className="mac-menu-glyph" />
            <span className="mac-menu-glyph" />
            <span className="mac-menu-item">9:41</span>
          </div>

          <div className="mac-switcher" role="img"
            aria-label={`Window switcher showing ${WINDOWS.length} windows, ${current.app} selected`}>
            <div className="mac-tiles">
              {WINDOWS.map((w, i) => (
                <div key={w.title} className={`mac-tile${i === index ? ' is-selected' : ''}`}>
                  <div className="mac-tile-thumb" style={{
                    background: `linear-gradient(150deg,
                      hsl(${w.hue} 55% 26%), hsl(${w.hue} 45% 14%))`,
                  }}>
                    <span className="mac-tile-badge" style={{ background: `hsl(${w.hue} 70% 55%)` }} />
                  </div>
                  <span className="mac-tile-label">{w.title}</span>
                </div>
              ))}
            </div>
            <p className="mac-caption">
              <strong>{current.app}</strong>
              <span className="mac-caption-dash">—</span>
              {current.title}
            </p>
          </div>
        </div>
      </div>

      <div className="mac-base"><span className="mac-notch-bottom" /></div>

      <p className="mac-hint">{t.hero.hint}</p>
    </div>
  )
}
